// packages/web/src/components/admin/replacement-add-popover.tsx
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Chip } from "@/components/vroom/chip";
import { ApproverPicker, type ApproverCandidate } from "./approver-picker.tsx";
import { useCreateReplacement } from "@/api/user-replacements/mutations";

export interface ReplacementAddPopoverProps {
  approverId: string;
  existingReplacementIds: string[];
  candidates: ApproverCandidate[];
}

export function ReplacementAddPopover({
  approverId,
  existingReplacementIds,
  candidates,
}: ReplacementAddPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [bidirectional, setBidirectional] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const createMut = useCreateReplacement();

  const exclude = [approverId, ...existingReplacementIds];

  async function onAdd() {
    if (!pickedId) {
      setPickError(t("admin.replacements.add-popover.pick-required"));
      return;
    }
    setPickError(null);
    try {
      await createMut.mutateAsync({ approverId, replacementId: pickedId });
      if (bidirectional) {
        await createMut.mutateAsync({ approverId: pickedId, replacementId: approverId });
      }
      toast.success(t("admin.replacements.add-popover.toast.added"));
      setOpen(false);
      setPickedId(null);
      setBidirectional(false);
    } catch (e) {
      const message =
        typeof e === "object" && e && "message" in e
          ? String((e as { message: unknown }).message)
          : "";
      toast.error(
        t("admin.replacements.add-popover.toast.failed", {
          message: message || t("admin.users.create-sheet.errors.unknown"),
        }),
      );
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Chip
          variant="dashed-plus"
          aria-label={t("admin.replacements.add-popover.trigger.label")}
          aria-hidden={open || undefined}
        >
          {t("admin.replacements.add-popover.trigger.label")}
        </Chip>
      </PopoverTrigger>
      <PopoverContent className="w-[min(320px,calc(100vw-2rem))] space-y-3" align="start">
        <div className="space-y-1.5">
          <ApproverPicker
            value={pickedId}
            onChange={(id) => {
              setPickedId(id);
              setPickError(null);
            }}
            candidates={candidates}
            exclude={exclude}
            placeholder={t("admin.replacements.add-popover.placeholder")}
          />
          {pickError ? <p className="text-[12px] text-vroom-red">{pickError}</p> : null}
        </div>
        <label className="flex items-center gap-2 text-[12.5px] text-ink-700">
          <Checkbox
            checked={bidirectional}
            onCheckedChange={(v) => setBidirectional(v === true)}
            aria-label={t("admin.replacements.add-popover.bidirectional.label")}
          />
          {t("admin.replacements.add-popover.bidirectional.label")}
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={createMut.isPending}>
            {t("common.buttons.cancel.label")}
          </Button>
          <Button variant="primary" onClick={onAdd} disabled={createMut.isPending}>
            {t("admin.replacements.add-popover.submit.label")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
